import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { HttpBackend, HttpClient, HttpEvent, HttpHeaders } from '@angular/common/http';
import { DialogState } from '../../models/dialog-state';
import { DialogsService } from '../../services/dialog.service';
import { BehaviorSubject } from 'rxjs';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { filter, first } from 'rxjs/operators';
import { Utilities } from '../../utilities';

@Component({
  selector: 'app-file-drop',
  templateUrl: './file-drop.component.html',
  styleUrls: ['./file-drop.component.scss'],
})
export class FileDropComponent implements OnInit, OnDestroy {
  @Input() dndEnabled: boolean = true;
  @Input() stopEventPropaginationForNgContent: boolean = false;
  @Input() chooseLabel: string = 'Выберите файл(ы)';
  @Input() url: string;
  @Input() multiple: boolean = false;
  @Input() accept: string;
  @Input() maxFileSize: number = null;
  @Input() loadOneByOne: boolean = true;
  @Input() withCredentials: boolean = true;
  @Input() httpHeaders: HttpHeaders = new HttpHeaders({
    accept: '*/*',
  });
  @Input() uploadedFileNames: string[] = [];
  @Input() isInputDisabled: boolean = false;
  @Input() removeItem = new BehaviorSubject<string>(null);
  @Output() beforeUpload: EventEmitter<any> = new EventEmitter();
  @Output() upload: EventEmitter<any> = new EventEmitter();
  @Output() error: EventEmitter<any> = new EventEmitter();

  public loadFile$ = new BehaviorSubject<boolean>(false);

  public indexLastUploaded: number = 0;

  files: any[] = [];

  hasFiles(): boolean {
    return this.files && this.files.length > 0;
  }

  private http: HttpClient;

  constructor(private httpBackend: HttpBackend, private dialogsService: DialogsService) {
    this.http = new HttpClient(httpBackend);
  }
  ngOnInit(): void {
    this.removeItem
      .pipe(
        filter((value) => !Utilities.isNull(value)),
        untilDestroyed(this)
      )
      .subscribe((removeItem) => {
        const indexItem = this.uploadedFileNames.indexOf(removeItem);
        if (indexItem !== -1) {
          this.uploadedFileNames.splice(indexItem, 1);
        }
      });
  }
  public onFileDropped(event) {
    this.prepareFilesList(event);
  }

  public fileBrowseHandler(event) {
    this.prepareFilesList(event.target.files);
  }

  public prepareFilesList(event) {
    for (const item of event) {
      this.files.push(item);
    }
    this.renameDuplicateFiles();
    this.uploadFiles(event);
  }

  private renameDuplicateFiles() {
    const duplicateFiles = []; //  наименования одинаковых файлов
    const replacedDuplicateFiles = []; //  новые наименования одинаковых файлов

    const updatedFiles = [];

    this.files.forEach((file) => {
      const lastDotIndex = file.name.lastIndexOf('.');
      let fileName = file.name.substring(0, lastDotIndex); // Получаем имя файла без расширения
      const fileExtension = file.name.substring(lastDotIndex); // Получаем расширение файла
      if (this.uploadedFileNames.includes(file.name)) {
        duplicateFiles.push(file.name);
        let index = 1;
        let newFileName = `${fileName}(${index})${fileExtension}`;

        while (this.uploadedFileNames.includes(newFileName)) {
          index++;
          newFileName = `${fileName}(${index})${fileExtension}`;
        }
        replacedDuplicateFiles.push(newFileName);
        fileName = newFileName; // Генерируем новое имя, добавляя "(1)", "(2)" и так далее
      } else {
        fileName = `${fileName}${fileExtension}`;
      }
      this.uploadedFileNames.push(fileName);
      this.indexLastUploaded++;

      const updatedFile = new File([file], fileName);
      updatedFiles.push(updatedFile);
    });
    if (duplicateFiles.length) {
      this.loadFile$.next(false);
      const duplicateFilesText = duplicateFiles.join(', ');
      const replacedDuplicateFilesText = replacedDuplicateFiles.join(', ');
      const text =
        duplicateFiles.length === 1
          ? `<div>Уже был загружен файл с именем ${duplicateFilesText}.
      Загружаемый файл будет переименован в ${replacedDuplicateFilesText}.</div>`
          : `<div>Уже были загружены файлы с именами ${duplicateFilesText}.
      Загружаемые файлы будут переименованы в ${replacedDuplicateFilesText}.</div>`;
      this.dialogsService.showWithHtmlContent({
        headerText: 'Предупреждение',
        content: [text],
        onAccept: (event: any) => {
          this.loadFile$.next(true);
        },
        onHide: (event: boolean) => {
          if (!event) {
            this.uploadedFileNames.splice(-this.indexLastUploaded);
            this.files = [];
            this.indexLastUploaded = 0;
          }
        },
      } as DialogState);
    } else {
      this.loadFile$.next(true);
    }
    this.files = updatedFiles;
  }

  public uploadFiles($event: any) {
    const load = this.loadFile$
      .pipe(
        first((value) => !!value && $event.length),
        untilDestroyed(this)
      )
      .subscribe(() => {
        if (this.hasFiles()) {
          this.beforeUpload.emit();
          this.isInputDisabled = true;
          if (!this.loadOneByOne) {
            this.multipleLoading($event);
          } else {
            this.uploadOneByOne($event);
          }
          this.files = [];
          this.isInputDisabled = false;
        }
        this.indexLastUploaded = 0;
        if (load) {
          load.unsubscribe();
        }
      });
  }

  private multipleLoading($event: any) {
    const formData = new FormData();
    let isFormDataEmpty = true;
    this.files.forEach((file) => {
      let textError = '';
      if (!this.validateFileTypes(file) || !file.size || !this.validateFileSize(file)) {
        textError = !this.validateFileTypes(file)
          ? `Разрешены только следующие типы файлов: ${this.accept}`
          : !file.size
          ? 'Не передано тело файла для загрузки.'
          : `Размер файла не может превышать ${this.maxFileSize / 1000000} МБ.`;
        this.emitSingleFileError(file, $event, textError);
        this._removeNotValidSingleFiles(file);
      } else {
        formData.append('file', file, file.name);
        isFormDataEmpty = false;
      }
    });
    if (!isFormDataEmpty) {
      this.http
        .post(this.url, formData, {
          withCredentials: this.withCredentials,
          headers: this.httpHeaders,
        })
        .subscribe(
          (event: HttpEvent<any>) => {
            this.upload.emit({
              originalEvent: event,
              files: this.files,
            });
          },
          (error) => {
            this.emitMultipleFilesError(this.files, $event, error.error);
            this._removeNotValidMultiFile($event);
          }
        );
    }
  }

  private _removeNotValidSingleFiles(file: File) {
    this.uploadedFileNames = this.uploadedFileNames.filter((item) => item !== file.name);
  }

  private _removeNotValidMultiFile(files: FileList) {
    const fileArray = Array.from(files);
    const filesToRemove = new Set(fileArray.map((value: File) => value.name));
    this.uploadedFileNames = this.uploadedFileNames.filter((item) => !filesToRemove.has(item));
  }

  private uploadOneByOne($event: any) {
    this.files.forEach((file) => {
      let textError = '';
      if (!this.validateFileTypes(file) || !file.size || !this.validateFileSize(file)) {
        textError = !this.validateFileTypes(file)
          ? `Разрешены только следующие типы файлов: ${this.accept}`
          : !file.size
          ? 'Не передано тело файла для загрузки.'
          : `Размер файла не может превышать ${this.maxFileSize / 1000000} МБ.`;
        this.emitSingleFileError(file, $event, textError);
        this._removeNotValidSingleFiles(file);
      } else {
        const formData = new FormData();
        formData.append('file', file, file.name);

        this.http
          .post(this.url, formData, {
            withCredentials: this.withCredentials,
            headers: this.httpHeaders,
          })
          .subscribe(
            (event: HttpEvent<any>) => {
              this.upload.emit({
                originalEvent: event,
                file: file,
              });
            },
            (error) => {
              this.emitSingleFileError(file, $event, error.error);
              this._removeNotValidSingleFiles(file);
            }
          );
      }
    });
  }

  private validateFileTypes(file: any) {
    const extension = '.' + file.name.split('.').pop();
    return this.accept.search(extension) > -1;
  }

  private validateFileSize(file: any) {
    return this.maxFileSize ? file.size < this.maxFileSize : true;
  }

  private emitSingleFileError(file: any, originalEvent?: any, message?: string) {
    this.error.emit({ originalEvent, message, file });
  }

  private emitMultipleFilesError(files: Array<any>, originalEvent?: any, message?: string) {
    this.error.emit({ originalEvent, message, files });
  }

  public stopEventPropagation(event) {
    if (this.stopEventPropaginationForNgContent) {
      event.stopPropagation();
      event.preventDefault();
    }
  }

  onClick(file: HTMLInputElement) {
    file.click();
  }

  ngOnDestroy(): void {}
}
